import { Box, useTheme } from "@mui/material";
import { useWavesurfer } from "@wavesurfer/react";
import { useCallback, useEffect, useMemo, useRef } from "react";
import { useHotkeys } from "react-hotkeys-hook";
import { WaveSurferOptions } from "wavesurfer.js";
import Timeline from "wavesurfer.js/dist/plugins/timeline";

import { AudioToolbar } from "./AudioToolbar";

interface AudioPreviewProps {
  url: string;
  // Normalized amplitudes in [0, 1], one per bar. When present, WaveSurfer renders these exact
  // bars instead of decoding the audio to derive its own; when absent it seeds bars from `url`.
  peaks?: number[] | null;
  hideToolbar?: boolean;
  hideTimeline?: boolean;
  playing?: boolean;
  enableHotkeys?: boolean;
  waveSurferOptions?: Partial<Omit<WaveSurferOptions, "container">>;
  onFinish?: () => void;
}

const DEFAULT_WAVE_SURFER_OPTIONS = {};

export const AudioPreview = (props: AudioPreviewProps) => {
  const {
    url,
    peaks,
    hideToolbar = false,
    hideTimeline = false,
    playing,
    enableHotkeys = true,
    waveSurferOptions = DEFAULT_WAVE_SURFER_OPTIONS,
    onFinish,
  } = props;
  const wavesurferContainerRef = useRef<HTMLDivElement>(null);
  const theme = useTheme();

  const plugins = useMemo(
    () => [
      ...(hideTimeline
        ? []
        : [
            Timeline.create({
              timeInterval: 0.1,
              primaryLabelInterval: 1,
              secondaryLabelInterval: 0.5,
              formatTimeCallback: (seconds: number) => `${seconds.toFixed(2)}s`,
            }),
          ]),
    ],
    [hideTimeline],
  );

  const wavesurferOptions = useMemo(
    () => ({
      container: wavesurferContainerRef,
      height: "auto" as const,
      waveColor: theme.palette.primary.light,
      progressColor: theme.palette.primary.main,
      url,
      // WaveSurfer takes one amplitude array per channel; our peaks are a single mono channel.
      ...(peaks?.length ? { peaks: [peaks] } : {}),
      plugins,
      ...waveSurferOptions,
    }),
    [theme, url, peaks, plugins, waveSurferOptions],
  );

  const { isPlaying, wavesurfer } = useWavesurfer(wavesurferOptions);

  const handleFinish = useCallback(() => {
    onFinish?.();
  }, [onFinish]);

  const handlePlayPause = () => {
    wavesurfer?.playPause();
  };

  // Handle preview toggle (space key)
  useHotkeys(
    "space",
    () => {
      if (!wavesurfer) return;

      wavesurfer.playPause();
    },
    {
      preventDefault: true,
      enabled: enableHotkeys,
    },
  );

  useEffect(() => {
    if (!wavesurfer) return;

    if (playing) {
      wavesurfer.play();
    } else {
      wavesurfer.pause();
    }
  }, [playing, wavesurfer]);

  useEffect(() => {
    if (!wavesurfer) return;

    wavesurfer.on("finish", handleFinish);

    return () => {
      wavesurfer.un("finish", handleFinish);
    };
  }, [wavesurfer, handleFinish]);

  return (
    <Box
      sx={{
        position: "relative",
        width: "100%",
        height: "100%",
        display: "flex",
        flexDirection: "column",
      }}
    >
      <div
        ref={wavesurferContainerRef}
        style={{
          width: "100%",
          height: hideToolbar ? "100%" : "calc(100% - 150px)",
        }}
      />
      {!hideToolbar && (
        <AudioToolbar isPlaying={isPlaying} onClick={handlePlayPause} />
      )}
    </Box>
  );
};
